import React, { createContext, useContext, useReducer, useEffect, useRef } from 'react';
import { clearQueryCacheOnAuthChange } from '../lib/queryClient';
import {
  clearAuthTokens,
  getAuthToken,
  setAuthToken,
  setRefreshToken,
} from '../lib/authToken';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  isPlatformAdmin?: boolean;
  isOfficeManager?: boolean;
  isManagingAttorney?: boolean;
  isEmailVerified: boolean;
  permissions: Record<string, boolean>;
  preferences: any;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  specialization?: string;
  barNumber?: string;
  hourlyRate?: string;
  timezone?: string;
  bio?: string;
  tenantId?: string;
  twoFactorEnabled?: boolean;
  requiresPasswordChange?: boolean;
}

export type LoginResult =
  | { status: 'success'; requiresPasswordChange?: boolean }
  | { status: 'requires2FA'; tempToken: string; user: Partial<User> }
  | { status: 'requires2FASetup'; tempToken: string; user: Partial<User> }
  | { status: 'requiresPasswordChange' };

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

type AuthAction =
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'LOGOUT' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'UPDATE_USER'; payload: User };

const initialState: AuthState = {
  user: null,
  token: getAuthToken(),
  isAuthenticated: false,
  isLoading: true,
};

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOGIN_SUCCESS':
      setAuthToken(action.payload.token);
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
      };
    case 'LOGOUT':
      clearAuthTokens();
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      };
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: action.payload,
      };
    default:
      return state;
  }
};

interface AuthContextType extends AuthState {
  login: (email: string, password: string, tenant?: string) => Promise<LoginResult>;
  verify2FA: (tempToken: string, code: string) => Promise<void>;
  complete2FASetup: (setupToken: string, code: string) => Promise<{ backupCodes: string[] }>;
  start2FASetup: (setupToken: string) => Promise<{ qrCode: string; manualEntryKey: string }>;
  changeFirstPassword: (newPassword: string) => Promise<void>;
  /** Apply tokens from SSO redirect (or any external auth) via /api/auth/me — clears tenant cache. */
  establishSessionFromToken: (token: string, refreshToken?: string | null) => Promise<User>;
  logout: () => void;
  updateUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  // Tracks last applied tenant so cold login does not double-clear React Query.
  const lastTenantIdRef = useRef<string | null | undefined>(undefined);

  const applySession = (user: User, token: string, refreshToken?: string) => {
    if (user?.role === 'client') {
      clearQueryCacheOnAuthChange();
      dispatch({ type: 'LOGOUT' });
      const err = new Error('Please sign in through the client portal');
      (err as any).code = 'USE_PORTAL_LOGIN';
      throw err;
    }
    // Drop any prior-tenant React Query cache before the new identity is live.
    // Without this, Team Management / Clients / Cases can briefly render another
    // firm's data after logout→login in the same SPA tab (staleTime cache hit).
    clearQueryCacheOnAuthChange();
    // Sync before LOGIN_SUCCESS so the tenant-switch effect does not treat cold
    // login (null → tenantId) as a second clear — that silently cancels Dashboard
    // queries mid-flight and leaves pages empty until a hard refresh.
    lastTenantIdRef.current = user.tenantId ?? null;
    if (refreshToken) {
      setRefreshToken(refreshToken);
    }
    dispatch({
      type: 'LOGIN_SUCCESS',
      payload: { user, token },
    });
  };

  /**
   * Complete an externally obtained access token (SSO redirect, etc.) the same
   * way as password login: clear prior-tenant cache, resolve /api/auth/me, apply session.
   */
  const establishSessionFromToken = async (token: string, refreshToken?: string | null) => {
    clearQueryCacheOnAuthChange();
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await fetch('/api/auth/me', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to establish session');
      }
      applySession(data.data.user, token, refreshToken || undefined);
      return data.data.user as User;
    } catch (error) {
      clearQueryCacheOnAuthChange();
      dispatch({ type: 'LOGOUT' });
      throw error;
    }
  };

  const login = async (email: string, password: string, tenant?: string): Promise<LoginResult> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      const loginData: any = { email, password };
      if (tenant) {
        loginData.tenant = tenant;
      }
      
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(loginData),
      });

      const data = await response.json();

      if (!response.ok) {
        const err = new Error(data.message || 'Login failed') as Error & { code?: string };
        err.code = data.code;
        throw err;
      }

      if (data.data?.requires2FA) {
        dispatch({ type: 'SET_LOADING', payload: false });
        return {
          status: 'requires2FA',
          tempToken: data.data.tempToken,
          user: data.data.user,
        };
      }

      if (data.data?.requires2FASetup) {
        dispatch({ type: 'SET_LOADING', payload: false });
        return {
          status: 'requires2FASetup',
          tempToken: data.data.tempToken,
          user: data.data.user,
        };
      }

      applySession(data.data.user, data.data.token, data.data.refreshToken);
      if (data.data.user?.requiresPasswordChange || data.data.user?.isFirstLogin) {
        return { status: 'requiresPasswordChange' };
      }
      return { status: 'success' };
    } catch (error) {
      dispatch({ type: 'SET_LOADING', payload: false });
      throw error;
    }
  };

  const verify2FA = async (tempToken: string, code: string) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await fetch('/api/2fa/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tempToken, token: code }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Verification failed');
      }
      applySession(data.data.user, data.data.token, data.data.refreshToken);
    } catch (error) {
      dispatch({ type: 'SET_LOADING', payload: false });
      throw error;
    }
  };

  const start2FASetup = async (setupToken: string) => {
    const response = await fetch('/api/2fa/setup', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${setupToken}`,
        'Content-Type': 'application/json',
      },
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Failed to start 2FA setup');
    }
    return {
      qrCode: data.data.qrCode,
      manualEntryKey: data.data.manualEntryKey,
    };
  };

  const complete2FASetup = async (setupToken: string, code: string) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await fetch('/api/2fa/verify-setup', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${setupToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token: code }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to verify 2FA setup');
      }

      if (data.data?.token && data.data?.user) {
        applySession(data.data.user, data.data.token, data.data.refreshToken);
      } else {
        dispatch({ type: 'SET_LOADING', payload: false });
      }

      return { backupCodes: data.data?.backupCodes || [] };
    } catch (error) {
      dispatch({ type: 'SET_LOADING', payload: false });
      throw error;
    }
  };

  const logout = () => {
    clearQueryCacheOnAuthChange();
    lastTenantIdRef.current = null;
    dispatch({ type: 'LOGOUT' });
  };

  // Only clear on a real tenant switch (non-null → different non-null).
  // Cold login / logout are handled in applySession / logout above.
  useEffect(() => {
    const nextTenantId = state.user?.tenantId ?? null;
    if (lastTenantIdRef.current === undefined) {
      lastTenantIdRef.current = nextTenantId;
      return;
    }
    const prev = lastTenantIdRef.current;
    if (prev && nextTenantId && prev !== nextTenantId) {
      clearQueryCacheOnAuthChange();
    }
    lastTenantIdRef.current = nextTenantId;
  }, [state.user?.tenantId]);

  const changeFirstPassword = async (newPassword: string) => {
    const token = getAuthToken() || state.token;
    if (!token) {
      throw new Error('Not authenticated');
    }
    const response = await fetch('/api/auth/first-time-password-change', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ newPassword }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Password change failed');
    }
    if (state.user) {
      dispatch({
        type: 'UPDATE_USER',
        payload: {
          ...state.user,
          requiresPasswordChange: false,
        },
      });
    }
  };

  const updateUser = (user: User) => {
    dispatch({ type: 'UPDATE_USER', payload: user });
  };

  // Check if user is authenticated on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = getAuthToken();
      if (token) {
        try {
          const response = await fetch('/api/auth/me', {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });

          if (response.ok) {
            const data = await response.json();
            if (data.data.user?.role === 'client') {
              clearQueryCacheOnAuthChange();
              dispatch({ type: 'LOGOUT' });
              return;
            }
            dispatch({
              type: 'LOGIN_SUCCESS',
              payload: {
                user: data.data.user,
                token,
              },
            });
          } else if (response.status === 429) {
            // Rate limited - don't retry immediately, just set loading to false
            console.warn('Rate limited on auth check');
            dispatch({ type: 'SET_LOADING', payload: false });
          } else {
            clearQueryCacheOnAuthChange();
            dispatch({ type: 'LOGOUT' });
          }
        } catch (error) {
          console.error('Auth check error:', error);
          clearQueryCacheOnAuthChange();
          dispatch({ type: 'LOGOUT' });
        }
      } else {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    checkAuth();
  }, []);

  const value: AuthContextType = {
    ...state,
    login,
    verify2FA,
    start2FASetup,
    complete2FASetup,
    changeFirstPassword,
    establishSessionFromToken,
    logout,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
